/**
 * Frequently bought together.
 *
 * Fetches complementary products through the Section Rendering API so the
 * tiles are built by Liquid, then owns only the interactive parts: the running
 * total and adding every selected item in one request.
 *
 * The whole block stays hidden unless recommendations actually come back, so a
 * product with none configured shows nothing rather than an empty heading.
 */
(() => {
  const root = document.querySelector('[data-bundle]');
  if (!root) return;

  const itemsEl = root.querySelector('[data-bundle-items]');
  const totalEl = root.querySelector('[data-bundle-total]');
  const addBtn = root.querySelector('[data-bundle-add]');
  const errorEl = root.querySelector('[data-bundle-error]');

  /**
   * Format cents using the shop's own money format, so currency and separators
   * match the rest of the page instead of being guessed here.
   */
  const MONEY_FORMAT = root.dataset.moneyFormat || '${{amount}}';

  const withDelimiters = (cents, precision, thousands, decimal) => {
    const value = (cents / 100).toFixed(precision);
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  const formatMoney = (cents) => {
    const match = MONEY_FORMAT.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return MONEY_FORMAT;

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return MONEY_FORMAT.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  const items = () => [...root.querySelectorAll('[data-bundle-item]')];

  const selected = () =>
    items().filter((item) => {
      const box = item.querySelector('[data-bundle-toggle]');
      return box && box.checked;
    });

  const paint = () => {
    const chosen = selected();
    const total = chosen.reduce((sum, item) => sum + parseInt(item.dataset.price, 10), 0);
    totalEl.textContent = formatMoney(total);
    // The page's own product is always included, so "nothing selected" can only
    // mean the extras are all off — still a valid one-item purchase.
    addBtn.disabled = chosen.length === 0;
  };

  const showError = (message) => {
    errorEl.textContent = message;
    errorEl.hidden = !message;
  };

  root.addEventListener('change', (event) => {
    const select = event.target.closest('[data-bundle-variants]');
    if (select) {
      const option = select.options[select.selectedIndex];
      const item = select.closest('[data-bundle-item]');
      item.dataset.price = option.dataset.price;
      item.dataset.variant = option.value;
      item.querySelector('[data-bundle-price]').textContent = formatMoney(
        parseInt(option.dataset.price, 10)
      );
    }
    paint();
  });

  addBtn.addEventListener('click', async () => {
    showError('');
    addBtn.disabled = true;

    const payload = selected().map((item) => ({
      id: parseInt(item.dataset.variant, 10),
      quantity: 1,
    }));

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: payload }),
      });

      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.description || `Add failed: ${response.status}`);
      }

      // Hand off to the cart drawer rather than duplicating its refresh logic.
      document.dispatchEvent(new CustomEvent('cart:added'));
    } catch (error) {
      console.error('[bundle]', error);
      showError(root.dataset.errorText || 'Could not add these to your cart.');
    } finally {
      addBtn.disabled = false;
      paint();
    }
  });

  /** Fetch one recommendations URL and return its rendered tiles. */
  const fetchTiles = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const recs = doc.querySelector('[data-bundle-recs]');
    return recs ? [...recs.querySelectorAll('[data-bundle-item]')] : [];
  };

  const load = async () => {
    try {
      let tiles = await fetchTiles(root.dataset.url);

      // Curated complementary products may not exist for this product; fall
      // back to Shopify's automatic related products rather than show nothing.
      if (tiles.length === 0 && root.dataset.fallbackUrl) {
        tiles = await fetchTiles(root.dataset.fallbackUrl);
      }

      // Still nothing to bundle with: leave the block hidden.
      if (tiles.length === 0) return;

      tiles.forEach((tile) => itemsEl.appendChild(tile));
      root.hidden = false;
      paint();
    } catch (error) {
      console.error('[bundle]', error);
    }
  };

  load();
})();
